import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { APP_BASENAME, resolveInAppNotificationUrl } from '@/utils/inAppNotification';
import { queryClient } from '@/resources/queryClient';
import type { InAppNotification } from '@/types/inAppNotification';

/**
 * Whether `to` addresses the route already on screen.
 *
 * Why we need this logic:
 * In case the user receive a notification related to data displayed on the current screen,
 * when the user click on the notification the data currently shown must be refreshed to display
 * the changes.
 */
const isCurrentRoute = (to: string) => {
  const target = new URL(`${APP_BASENAME}${to}`, window.location.origin);
  const here = new URL(window.location.href);
  const samePath = target.pathname.replace(/\/$/, '') === here.pathname.replace(/\/$/, '');
  const sameParams = [...target.searchParams]
    .every(([key, value]) => here.searchParams.get(key) === value);

  return samePath && sameParams;
};

/**
 * Refreshes what is on screen before following an internal notification link.
 *
 * If the link points at the current route, navigating is a no-op, and the
 * notification exists precisely because something on this page changed.
 * Invalidating refetches the mounted queries only, so this refreshes what is on
 * screen without the state loss of a reload.
 */
export const refreshInAppNotificationTarget = () => {
  // TODO: (TECH_DEBT: https://greyscout.atlassian.net/browse/GS2-2612)
  //       We should target a specific query instead of invalidating all queries
  queryClient.invalidateQueries();
};

export const useInAppNotificationNavigation = () => {
  const navigate = useNavigate();

  return useCallback((notification: InAppNotification) => {
    const resolved = resolveInAppNotificationUrl(
      notification.url, notification.referenceType, notification.referenceId,
    );

    if (resolved.kind === 'internal') {
      refreshInAppNotificationTarget();

      if (!isCurrentRoute(resolved.to)) {
        navigate(resolved.to);
      }

      return;
    }

    if (resolved.kind === 'external') {
      window.open(resolved.href, '_blank', 'noopener,noreferrer');
    }
  }, [navigate]);
};
